import {Link} from 'react-router-dom';
import clsx from 'clsx';
import React, {useMemo} from 'react';
import {
  Album,
  getAlbumRouteAlbumId,
  getDerivedAlbumId,
  isDerivedAlbum,
} from '@app/web-player/albums/album';
import {Artist} from '@app/web-player/artists/artist';
import {slugifyString} from '@ui/utils/string/slugify-string';
import {getBootstrapData} from '@ui/bootstrap-data/bootstrap-data-store';
import { isIOSOrAndroid } from '@common/core/platform-detection';
import { StackTrigger } from '@ui/overlays/stack-navigator/stack-trigger';
import { AlbumPage } from './album-page';
import { AlbumImage } from './album-image/album-image';

interface AlbumLinkProps {
  album: Album;
  artist?: Artist;
  className?: string;
  classNameImage?: string;
  target?: string;
  size?: string;
  openInDialogTrigger?: boolean;
  closePlayerOverlayOnOpen?: boolean;
}
export function AlbumLink({
  album,
  artist,
  className,
  target,
  openInDialogTrigger = true,
  closePlayerOverlayOnOpen = false,
}: AlbumLinkProps) {
  if (!artist && album.artists) {
    artist = album.artists[0];
  }
  const uri = useMemo(() => {
    return getAlbumLink(album, {artist});
  }, [artist, album]);

  return (
    <>
      {!isIOSOrAndroid() || !openInDialogTrigger ? (
        <Link
          target={target}
          className={clsx(
            'overflow-x-hidden overflow-ellipsis outline-none hover:underline focus-visible:underline',
            className,
          )}
          to={uri}
        >
          {album.name}
        </Link>
      ) : (
        <StackTrigger
          moveFocusToDialog={false}
          title={album.name}
          closePlayerOverlayOnOpen={closePlayerOverlayOnOpen}
        >
          <div className={clsx(
            'overflow-x-hidden overflow-ellipsis outline-none hover:underline focus-visible:underline',
            className,
          )}>{album.name}</div>
          <AlbumPage album={album} />
        </StackTrigger>
      )}
    </>
  );
}

export function AlbumImageLink({
  album,
  artist,
  className,
  target,
  size,
  classNameImage,
  openInDialogTrigger = true,
  closePlayerOverlayOnOpen = false,
}: AlbumLinkProps) {
  if (!artist && album.artists) {
    artist = album.artists[0];
  }
  const uri = useMemo(() => {
    return getAlbumLink(album, {artist});
  }, [artist, album]);

  return (
    <>
      {!isIOSOrAndroid() || !openInDialogTrigger ? (
        <Link
          target={target}
          className={clsx(
            'overflow-x-hidden overflow-ellipsis outline-none hover:underline focus-visible:underline',
            className,
          )}
          to={uri}
        >
          <AlbumImage
            album={album}
            className={classNameImage}
            size={size}
          />
        </Link>
      ) : (
        <StackTrigger
          moveFocusToDialog={false}
          title={album.name}
          closePlayerOverlayOnOpen={closePlayerOverlayOnOpen}
        >
          <div className={clsx(
            'overflow-x-hidden overflow-ellipsis outline-none hover:underline focus-visible:underline',
            className,
          )}>
            <AlbumImage
              album={album}
              className="flex-shrink-0 rounded"
              size="w-70 h-70"
            />
          </div>
          <AlbumPage album={album} />
        </StackTrigger>
      )}
    </>
  );
}

export function getAlbumLink(
  album: Album,
  options: {artist?: Artist; absolute?: boolean} = {},
) {
  const artist = options.artist || album.artists?.[0];
  const artistName = slugifyString(artist?.name || 'Various Artists');
  const albumName = slugifyString(album.name);
  const albumId = getAlbumRouteAlbumId(album);
  const derivedId = getDerivedAlbumId(album);
  let link = derivedId
    ? `/album/${albumId}/${derivedId}/${artistName}/${albumName}`
    : `/album/${albumId}/${artistName}/${albumName}`;
  if (options.absolute) {
    link = `${getBootstrapData().settings.base_url}${link}`;
  }
  return link;
}
